import React from "react";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

import type { ClientThreadMessage } from "../../services/clientSmsService";
import { buildStream, isPendingStatus, isStaleSending, type StreamEntry } from "./threadStreamModel";
import { bubbleColors } from "./ClientMessagesPanel";

/**
 * Accent text that survives BOTH modes.
 *
 * The render gate caught `primary.main` as a text colour on the outbound
 * group's tinted background: 3.21:1 in dark mode. `primary.main` is tuned to
 * be a FILL that white sits on, not a foreground — the same confusion that
 * produced the 4.47:1 outbound bubble the earlier hotfix had to fix. In dark
 * mode the lighter shade is the legible one; in light mode the darker.
 */
function accentText(theme: Theme): string {
  return theme.palette.mode === "dark"
    ? theme.palette.primary.light
    : theme.palette.primary.dark;
}

/**
 * PKT-0465 · Status tones that survive both modes on the NON-bubble surfaces.
 * `error.main` / `warning.main` are FILLS, not foregrounds — the same
 * confusion accentText documents. Measured before this fix: 3.82:1 (dark) and
 * 3.90:1 (light) for the failure red on the flat surface. Same shape as
 * accentText: the light shade carries on dark, the dark shade on light.
 */
function errorText(theme: Theme): string {
  return theme.palette.mode === "dark"
    ? theme.palette.error.light
    : theme.palette.error.dark;
}

function warningText(theme: Theme): string {
  // Amber is the hard case on light surfaces: this theme's `warning.dark`
  // (#b45309) measures 4.05:1 on the flat outbound surface — still short of
  // AA. One step further down the same ramp (Tailwind amber-800, the ramp
  // this palette is drawn from) clears it. Dark mode keeps the light shade.
  return theme.palette.mode === "dark"
    ? theme.palette.warning.light
    : "#92400e";
}

/**
 * THE THREAD STREAM.
 *
 * Desktop drops bubbles entirely — flat, full-width, grouped rows, the way
 * Slack renders a channel. Bubbles cap a message at ~76% of the column and
 * give every message its own island, which on a wide pane wastes the two
 * things a working operator needs most: horizontal room and vertical density.
 *
 * MOBILE KEEPS BUBBLES, deliberately. In a ~375px column, alternating sides is
 * the cheapest possible direction signal — no name label, no colour key, no
 * cognitive cost — and it matches every SMS app the operator already uses. The
 * flat layout needs a name per group, which is overhead on a narrow screen
 * where there are only ever two speakers. Same principle, different constraint.
 *
 * What BOTH modes gain:
 *   · grouping — a run of texts is one turn with ONE timestamp, not five
 *   · quiet status — "Delivered" is muted text on the last message of a group;
 *     "Failed" stays loud because it is real, and carries Retry
 *   · system output as event rows rather than dashed bubbles, so an automated
 *     bounce never reads as something a human typed
 *
 * Colours resolve through the theme on every path (`bubbleColors` and the
 * palette tokens below). The estate shipped invisible text twice from a fixed
 * background paired with a palette-following foreground; the contrast gate
 * next door renders this component and measures it.
 */

export interface ThreadStreamProps {
  messages: ClientThreadMessage[];
  unreadIds: Set<string>;
  onRetry?: (body: string) => void;
  /** Test seam: the clock the stale-sending rule reads. Defaults to Date.now(). */
  nowMs?: number;
  /** Test seam: force a layout instead of reading the viewport. */
  forceLayout?: "flat" | "bubbles";
}

function timeLabel(raw?: string | null): string {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function failedStatus(status?: string | null): boolean {
  return status === "failed" || status === "undelivered";
}

/**
 * Which messages in a group show their own delivery state.
 *
 * COLLAPSE IDENTICAL, SHOW EVERY CHANGE. A run of five delivered texts is one
 * "Delivered" — that is the density win. A run whose states differ shows each
 * one, because collapsing them would report a state that is not true of every
 * message in the turn.
 *
 * Two rules got here, both caught by tests rather than by reading the code:
 * "last only" hid a FAILURE behind a later success (a failed send is the one
 * thing that must never be swallowed), and it also hid a delivered message
 * behind a still-sending one.
 */
function showStatusFor(
  m: { status?: string | null; created_at?: string | null },
  isLast: boolean,
  next: { status?: string | null } | undefined,
  nowMs: number
): boolean {
  // A stale-sending row joins failure in the never-swallowed set: hiding it
  // behind a later success is exactly the eleven-hour lie this rule removes.
  if (isLast || m.status === "outcome_unknown" || failedStatus(m.status) || isStaleSending(m, nowMs)) return true;
  return !!next && next.status !== m.status;
}

const SYSTEM_LABEL: Record<string, string> = {
  media_bounce: "Auto-reply · media not supported",
  help_reply: "Auto-reply · HELP",
};

/** Delivery state, as quiet as it can be while still being honest. */
function DeliveryStatus({
  status,
  body,
  createdAt,
  nowMs,
  onRetry,
  bubbleFg,
}: {
  status?: string | null;
  body: string;
  createdAt?: string | null;
  nowMs: number;
  onRetry?: (body: string) => void;
  /**
   * PKT-0465 · Set when this caption renders INSIDE a brand-fill bubble — the
   * surface that produced 1.06:1 red-on-brand. Inside a bubble, EVERY caption
   * colour is the bubble's already-verified foreground and tone is carried by
   * the icon alone: no palette tone survives all four theme×fill cells, and
   * the bubble pair is the one combination this app has actually proven.
   */
  bubbleFg?: string;
}) {
  if (!status) return null;
  if (status === "outcome_unknown") return <Typography variant="caption" color="inherit" data-testid="sms-delivery-status">Send outcome unknown — check before resending</Typography>;
  const failed = failedStatus(status);
  // ELEVEN-HOUR "Sending…" (observed live 2026-08-07): a row persisted with a
  // pending status and no twilio_sid has no terminal transition coming — the
  // status webhook matches on twilio_sid. Past STALE_SENDING_MS this is not
  // in flight; saying so would tell the operator a message is on its way to a
  // client when it is not. Amber, not red: "failed" is Twilio's verdict, this
  // is the absence of one. And "Not CONFIRMED sent", not "Not sent": the
  // thread payload does not expose twilio_sid, so this rule cannot tell the
  // orphan (sid NULL, Twilio never called) from a genuinely queued message a
  // rate-limited long code delivers late — an affirmative "Not sent" would be
  // FALSE for the latter and invite a duplicate text to a real client
  // (adversarial review finding #7, 2026-08-07).
  const stale = isStaleSending({ status, created_at: createdAt }, nowMs);
  const pending = !stale && isPendingStatus(status);
  // `scaffold` means SMS_PROVIDER_MODE was not live: the adapter LOGGED the
  // message instead of POSTing it. Nothing reached a phone. The old copy said
  // "Sent (test mode)" and the redesign briefly said "Sending…" — both imply
  // something left the building. It did not.
  const scaffold = status === "scaffold";

  if (stale) {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ mt: 0.25 }}
        data-testid="sms-delivery-status"
      >
        <ErrorOutlineIcon sx={{ fontSize: 14, color: bubbleFg ?? ((t: Theme) => warningText(t)) }} />
        <Typography variant="caption" sx={{ color: bubbleFg ?? ((t) => warningText(t)) }}>
          Not confirmed sent
        </Typography>
        {onRetry && (
          <Typography
            variant="caption"
            component="button"
            data-testid="sms-retry"
            onClick={() => onRetry(body)}
            sx={{
              color: bubbleFg ?? ((t) => accentText(t)),
              background: "none",
              border: "none",
              p: 0,
              cursor: "pointer",
              textDecoration: "underline",
              font: "inherit",
            }}
          >
            <ReplayIcon sx={{ fontSize: 12, verticalAlign: "-2px", mr: 0.25 }} />
            Retry
          </Typography>
        )}
      </Stack>
    );
  }

  if (failed) {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ mt: 0.25 }}
        data-testid="sms-delivery-status"
      >
        <ErrorOutlineIcon sx={{ fontSize: 14, color: bubbleFg ?? ((t: Theme) => errorText(t)) }} />
        <Typography variant="caption" sx={{ color: bubbleFg ?? ((t) => errorText(t)) }}>
          Failed to send
        </Typography>
        {onRetry && (
          <Typography
            variant="caption"
            component="button"
            data-testid="sms-retry"
            onClick={() => onRetry(body)}
            sx={{
              color: bubbleFg ?? ((t) => accentText(t)),
              background: "none",
              border: "none",
              p: 0,
              cursor: "pointer",
              textDecoration: "underline",
              font: "inherit",
            }}
          >
            <ReplayIcon sx={{ fontSize: 12, verticalAlign: "-2px", mr: 0.25 }} />
            Retry
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{ mt: 0.25 }}
      data-testid="sms-delivery-status"
    >
      {pending || scaffold ? (
        <ScheduleIcon sx={{ fontSize: 13, color: bubbleFg ?? "text.secondary" }} />
      ) : status === "delivered" ? (
        <DoneAllIcon sx={{ fontSize: 13, color: bubbleFg ?? "text.secondary" }} />
      ) : (
        <CheckIcon sx={{ fontSize: 13, color: bubbleFg ?? "text.secondary" }} />
      )}
      <Typography variant="caption" sx={{ color: bubbleFg ?? "text.secondary" }}>
        {scaffold
          ? "Not sent (test mode)"
          : pending
          ? "Sending…"
          : status === "delivered"
          ? "Delivered"
          : "Sent"}
      </Typography>
    </Stack>
  );
}

/**
 * The unread badge — a plain pill, NOT MUI's Chip.
 *
 * Two measured failures got it here, both caught by the render gate rather
 * than by reading the code:
 *   1. `color="info"` measured 2.77:1 in dark mode — below AA-LARGE.
 *   2. Setting `bgcolor` + `color` on a Chip still produced **1.40:1** in
 *      light mode, because this app's theme overrides `MuiChip` with a 15%
 *      alpha background: the fill I asked for was never solid, and the label
 *      colour I set on the root never reached the label.
 *
 * Fighting a theme override with specificity would leave a badge whose
 * legibility depends on which stylesheet wins. A Box renders exactly what it
 * is told, which is the whole requirement for a thing whose job is to be seen.
 */
function UnreadChip({ sx }: { sx?: object }) {
  const theme = useTheme();
  const { bg, fg } = bubbleColors(theme, false, false);
  return (
    <Box
      component="span"
      data-testid="sms-unread-chip"
      sx={{
        display: "inline-block",
        px: 0.75,
        py: 0.125,
        borderRadius: 1,
        fontSize: 10,
        fontWeight: 500,
        lineHeight: 1.6,
        backgroundColor: bg,
        color: fg,
        ...sx,
      }}
    >
      New
    </Box>
  );
}

/** A thin centred rule. Used for both day boundaries and system events. */
function RuleRow({
  children,
  testId,
}: {
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ my: 1.25, px: 1 }}
      data-testid={testId}
    >
      <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
      {children}
      <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
    </Stack>
  );
}

/** System output — Tier 2 of the event vocabulary: quiet, centred, never a bubble. */
function SystemEventRow({ message }: { message: ClientThreadMessage }) {
  return (
    <RuleRow testId={`sms-system-${message.id}`}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ maxWidth: "80%" }}>
        <SmartToyOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
          {SYSTEM_LABEL[message.kind] || "Auto-reply"}
          {message.created_at ? ` · ${timeLabel(message.created_at)}` : ""}
        </Typography>
      </Stack>
    </RuleRow>
  );
}

/** FLAT (desktop): one header per turn, then the lines. */
function FlatGroup({
  entry,
  unreadIds,
  onRetry,
  nowMs,
}: {
  entry: StreamEntry;
  unreadIds: Set<string>;
  onRetry?: (body: string) => void;
  nowMs: number;
}) {
  const theme = useTheme();
  const accent = accentText(theme);
  const outbound = entry.direction === "out";
  // PKT-0506 (NG-0073, ruled 2026-08-13): the group timestamp is the LAST
  // message's time in BOTH layouts. This header used the FIRST message's time
  // while the bubbles layout used the LAST, so the same thread showed two
  // different times depending on window width.
  const last = entry.messages[entry.messages.length - 1];
  const hasUnread = entry.messages.some((m) => unreadIds.has(m.id));

  return (
    <Box
      data-testid={`sms-group-${entry.key}`}
      sx={{
        px: 1.5,
        py: 0.75,
        borderLeft: 2,
        borderColor: outbound ? accent : "transparent",
        bgcolor: outbound ? "action.hover" : "transparent",
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 500, color: outbound ? accent : "text.primary" }}
        >
          {outbound ? "You" : "Client"}
        </Typography>
        {hasUnread && <UnreadChip />}
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="sms-group-time"
        >
          {timeLabel(last.created_at)}
        </Typography>
      </Stack>

      {entry.messages.map((m, i) => (
        <Box key={m.id} data-testid={`sms-message-${m.id}`}>
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5, color: "text.primary" }}
          >
            {m.body}
          </Typography>
          {m.history_source && <Typography variant="caption" color="text.secondary">{m.history_source}</Typography>}
          {m.num_media > 0 && (
            <Typography variant="caption" color="text.secondary">
              {m.num_media} media attachment{m.num_media > 1 ? "s" : ""} (not stored)
            </Typography>
          )}
          {outbound &&
            showStatusFor(m, i === entry.messages.length - 1, entry.messages[i + 1], nowMs) && (
            <DeliveryStatus status={m.status} body={m.body} createdAt={m.created_at} nowMs={nowMs} onRetry={onRetry} />
          )}
        </Box>
      ))}
    </Box>
  );
}

/** BUBBLES (mobile): grouped, so a run of texts is one stack with one time. */
function BubbleGroup({
  entry,
  unreadIds,
  onRetry,
  nowMs,
}: {
  entry: StreamEntry;
  unreadIds: Set<string>;
  onRetry?: (body: string) => void;
  nowMs: number;
}) {
  const theme = useTheme();
  const inbound = entry.direction === "in";
  const last = entry.messages[entry.messages.length - 1];
  const hasUnread = entry.messages.some((m) => unreadIds.has(m.id));
  // Reuse the PROVEN pair. Rolling a new one here produced
  // rgba(0,0,0,0.87) on a dark surface at 1.82:1 — the exact class that
  // shipped invisible text to production twice.
  const { bg, fg } = bubbleColors(theme, inbound, false);

  return (
    <Box
      data-testid={`sms-group-${entry.key}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: inbound ? "flex-start" : "flex-end",
        px: 1,
        py: 0.5,
      }}
    >
      {hasUnread && <UnreadChip sx={{ mb: 0.25 }} />}
      <Stack spacing={0.25} sx={{ maxWidth: "82%", alignItems: inbound ? "flex-start" : "flex-end" }}>
        {entry.messages.map((m, i) => (
          <Box
            key={m.id}
            data-testid={`sms-message-${m.id}`}
            sx={{ px: 1.5, py: 0.75, borderRadius: 2, bgcolor: bg, color: fg }}
          >
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "inherit" }}>
              {m.body}
            </Typography>
            {m.history_source && <Typography variant="caption" color="inherit">{m.history_source}</Typography>}
            {m.num_media > 0 && (
              <Typography variant="caption" sx={{ color: "inherit", opacity: 0.85 }}>
                {m.num_media} media attachment{m.num_media > 1 ? "s" : ""} (not stored)
              </Typography>
            )}
            {!inbound &&
              i !== entry.messages.length - 1 &&
              showStatusFor(m, false, entry.messages[i + 1], nowMs) && (
                <DeliveryStatus status={m.status} body={m.body} createdAt={m.created_at} nowMs={nowMs} onRetry={onRetry} bubbleFg={fg} />
              )}
          </Box>
        ))}
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="sms-group-time"
        >
          {timeLabel(last.created_at)}
        </Typography>
        {!inbound && (
          <DeliveryStatus status={last.status} body={last.body} createdAt={last.created_at} nowMs={nowMs} onRetry={onRetry} />
        )}
      </Stack>
    </Box>
  );
}

export default function ThreadStream({
  messages,
  unreadIds,
  onRetry,
  forceLayout,
  nowMs: nowMsProp,
}: ThreadStreamProps) {
  const isWide = useMediaQuery((t: Theme) => t.breakpoints.up("md"));
  const flat = forceLayout ? forceLayout === "flat" : isWide;
  const entries = buildStream(messages);
  // No ticker on purpose: this evaluates only when the component renders. The
  // sole consumer (ClientMessagesPanel) polls the thread every 15s and always
  // sets fresh state, so a message crossing STALE_SENDING_MS flips within one
  // tick. A future consumer that does NOT poll must supply its own re-render
  // cadence (or a ticker), or staleness will surface late.
  const nowMs = nowMsProp ?? Date.now();

  return (
    <>
      {entries.map((entry) => (
        <React.Fragment key={entry.key}>
          {entry.dayLabel && (
            <RuleRow testId={`sms-day-${entry.key}`}>
              <Typography variant="caption" color="text.secondary">
                {entry.dayLabel}
              </Typography>
            </RuleRow>
          )}
          {entry.kind === "system" ? (
            <SystemEventRow message={entry.messages[0]} />
          ) : flat ? (
            <FlatGroup entry={entry} unreadIds={unreadIds} onRetry={onRetry} nowMs={nowMs} />
          ) : (
            <BubbleGroup entry={entry} unreadIds={unreadIds} onRetry={onRetry} nowMs={nowMs} />
          )}
        </React.Fragment>
      ))}
    </>
  );
}
